import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  Text,
  VStack,
  chakra,
} from "@chakra-ui/react";
import { useReq } from "@larner.dev/use-req";
import {
  ContactPreferenceEnum,
  UserWithMediaAndGroups,
} from "@greenflagdate/shared";
import { Icon, type IconName } from "../../../components/Icon/Icon";
import { apiReq } from "../../../lib/apiReq";
import { store, useStoreData } from "../../../lib/store/store";
import { ActionType } from "../../../lib/store/storeActions";
import { toaster } from "../../../lib/toaster";
import { VerifyPhoneNumberDialog } from "../../../components/VerifyPhoneNumberDialog/VerifyPhoneNumberDialog";
import { GroupMeInfoDialog } from "../../../components/GroupMeInfoDialog/GroupMeInfoDialog";
import { ModalPage } from "../../../components/ModalPage";

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Text
      as="div"
      fontSize="10px"
      letterSpacing="0.14em"
      textTransform="uppercase"
      color="fg.muted"
      fontWeight="semibold"
      mb="10px"
    >
      {children}
    </Text>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <Box
      bg="bg.canvas"
      border="1px solid"
      borderColor="border.subtle"
      borderRadius="sm"
      boxShadow="card"
      overflow="hidden"
    >
      {children}
    </Box>
  );
}

function Toggle({
  checked,
  onChange,
  disabled,
  ariaLabel,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  ariaLabel: string;
}) {
  return (
    <chakra.button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => !disabled && onChange()}
      disabled={disabled}
      position="relative"
      w="44px"
      h="26px"
      borderRadius="pill"
      bg={checked ? "bg.brand" : "#D6D9DD"}
      border="0"
      cursor={disabled ? "not-allowed" : "pointer"}
      transition="background 150ms ease"
      flexShrink={0}
      opacity={disabled ? 0.55 : 1}
      _hover={!disabled ? { filter: "brightness(1.04)" } : undefined}
    >
      <Box
        position="absolute"
        top="3px"
        left={checked ? "21px" : "3px"}
        w="20px"
        h="20px"
        borderRadius="full"
        bg="white"
        boxShadow="0 1px 3px rgba(0,0,0,0.25)"
        transition="left 150ms ease"
      />
    </chakra.button>
  );
}

function ChannelRow({
  icon,
  title,
  detail,
  action,
  checked,
  onToggle,
  disabled,
  isFirst,
}: {
  icon: IconName;
  title: string;
  detail?: React.ReactNode;
  action?: React.ReactNode;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
  isFirst?: boolean;
}) {
  return (
    <Flex
      align="center"
      gap="14px"
      p="14px 16px"
      borderTop={isFirst ? "0" : "1px solid"}
      borderTopColor="border.subtle"
    >
      <Flex
        w="36px"
        h="36px"
        borderRadius="full"
        align="center"
        justify="center"
        flexShrink={0}
        bg="rgba(11,128,205,0.12)"
        color="bg.friends"
      >
        <Icon name={icon} boxSize="16px" />
      </Flex>
      <Box flex={1} minW="0">
        <Text
          fontSize="14px"
          fontWeight="semibold"
          color="fg.default"
          lineHeight="1.25"
        >
          {title}
        </Text>
        {detail && (
          <Box mt="2px" fontSize="12px" color="fg.muted" lineHeight="1.35">
            {detail}
          </Box>
        )}
        {action && <Box mt="8px">{action}</Box>}
      </Box>
      <Toggle
        checked={checked}
        onChange={onToggle}
        disabled={disabled}
        ariaLabel={`Share ${title.toLowerCase()}`}
      />
    </Flex>
  );
}

function InstagramHandleInput({
  value,
  onSave,
  disabled,
}: {
  value?: string;
  onSave: (v?: string) => Promise<void>;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  const commit = async () => {
    const trimmed = draft.trim();
    if (trimmed === (value ?? "")) return;
    setSaving(true);
    try {
      await onSave(trimmed || undefined);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Flex
      align="center"
      gap="2"
      p="9px 12px"
      border="1px solid"
      borderColor="border.default"
      borderRadius="xs"
      bg="bg.canvas"
      opacity={disabled ? 0.55 : 1}
      _focusWithin={{
        borderColor: "border.brand",
        boxShadow: "0 0 0 3px rgba(57,184,125,0.25)",
      }}
    >
      <Text as="span" fontSize="13px" color="fg.muted">
        @
      </Text>
      <chakra.input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder="greenflagdate"
        disabled={disabled || saving}
        flex={1}
        fontSize="13px"
        bg="transparent"
        border="0"
        outline="none"
        color="fg.default"
        _placeholder={{ color: "fg.muted", opacity: 0.55 }}
      />
    </Flex>
  );
}

export function Contact() {
  const storeData = useStoreData();
  const user = storeData.user;

  const [updateUserReq, updateUserReqState] =
    useReq<UserWithMediaAndGroups>(apiReq);
  const [showVerifyPhoneDialog, setShowVerifyPhoneDialog] = useState(false);
  const [showGroupmeInfo, setShowGroupmeInfo] = useState(false);

  const prefs = user?.contact_preferences ?? [];
  const has = (p: ContactPreferenceEnum) => prefs.includes(p);

  const togglePreference = useCallback(
    async (pref: ContactPreferenceEnum) => {
      const next = has(pref) ? prefs.filter((p) => p !== pref) : [...prefs, pref];
      const response = await updateUserReq.put("/user/attributes", {
        contact_preferences: next,
      });
      if (response.success) {
        store.dispatch({
          type: ActionType.UpdateUser,
          params: { user: response.result },
        });
      } else {
        toaster.create({
          type: "error",
          title: "Couldn't update your contact preferences.",
        });
      }
    },
    [prefs, updateUserReq]
  );

  const saveInstagramHandle = useCallback(
    async (value?: string) => {
      const response = await updateUserReq.put("/user/attributes", {
        instagram_handle: value,
      });
      if (response.success) {
        store.dispatch({
          type: ActionType.UpdateUser,
          params: { user: response.result },
        });
      } else {
        toaster.create({
          type: "error",
          title: "Couldn't save your Instagram handle.",
        });
      }
    },
    [updateUserReq]
  );

  const saving = updateUserReqState.loading;

  return (
    <ModalPage
      motionKey="settings-contact-modal"
      title="Contact Preferences"
      fallback="/settings"
      bodyBg="bg.canvas"
    >
    <Flex direction="column" minH="100%" w="100%">
      <Box flex="1" w="100%" maxW="40rem" mx="auto" pt="18px" pb="2rem">
        <Box px="4" mb="2">
          <Text fontSize="13px" color="fg.muted" lineHeight="1.5">
            When you have a mutual match, the channels you turn on here will be
            shared with the person you matched with.
          </Text>
        </Box>

        <Box px="4" pt="4">
          <Eyebrow>Channels</Eyebrow>
          <Card>
            <ChannelRow
              isFirst
              icon="groupme"
              title="GroupMe"
              detail={
                <chakra.button
                  type="button"
                  onClick={() => setShowGroupmeInfo(true)}
                  bg="transparent"
                  border="0"
                  p="0"
                  color="fg.brand"
                  fontSize="12px"
                  fontWeight="semibold"
                  cursor="pointer"
                  textDecoration="underline"
                  _hover={{ filter: "brightness(0.95)" }}
                >
                  Join GroupMe channel
                </chakra.button>
              }
              checked={has(ContactPreferenceEnum.Groupme)}
              onToggle={() => togglePreference(ContactPreferenceEnum.Groupme)}
              disabled={saving}
            />
            <ChannelRow
              icon="mobile"
              title="Phone number"
              detail={
                user?.phone_number || user?.phone_number_to_validate || (
                  <Text as="span" color="fg.muted">
                    Add a phone number to share by text
                  </Text>
                )
              }
              checked={has(ContactPreferenceEnum.Phone)}
              onToggle={() => {
                if (user?.phone_number) {
                  togglePreference(ContactPreferenceEnum.Phone);
                } else {
                  setShowVerifyPhoneDialog(true);
                }
              }}
              disabled={saving}
            />
            <ChannelRow
              icon="envelope"
              title="Email"
              detail={user?.email}
              checked={has(ContactPreferenceEnum.Email)}
              onToggle={() => togglePreference(ContactPreferenceEnum.Email)}
              disabled={saving}
            />
            <ChannelRow
              icon="instagram"
              title="Instagram"
              action={
                <InstagramHandleInput
                  value={user?.instagram_handle}
                  onSave={saveInstagramHandle}
                  disabled={!has(ContactPreferenceEnum.Instagram) || saving}
                />
              }
              checked={has(ContactPreferenceEnum.Instagram)}
              onToggle={() => togglePreference(ContactPreferenceEnum.Instagram)}
              disabled={saving}
            />
          </Card>
        </Box>

        {updateUserReqState.error && (
          <Box px="4" pt="3">
            <HStack
              gap="2"
              p="10px 12px"
              bg="#FEF5F5"
              border="1px solid"
              borderColor="fg.danger"
              borderRadius="xs"
            >
              <Icon name="x" boxSize="14px" color="fg.danger" />
              <Text fontSize="12px" color="fg.danger">
                {updateUserReqState.error.code ||
                  "Something went wrong saving your changes."}
              </Text>
            </HStack>
          </Box>
        )}

        <VStack pt="3" />
      </Box>

      <VerifyPhoneNumberDialog
        isShown={showVerifyPhoneDialog}
        offerTextOptIn
        onClose={() => setShowVerifyPhoneDialog(false)}
        onSuccess={() => togglePreference(ContactPreferenceEnum.Phone)}
      />
      <GroupMeInfoDialog
        isShown={showGroupmeInfo}
        onCloseComplete={() => setShowGroupmeInfo(false)}
      />
    </Flex>
    </ModalPage>
  );
}
